<template>
  <b-container fluid>
    <div class="mt-3">
      <p
        style="margin-bottom: -7px">
        Packages
      </p>
      <div role="tablist">
        <b-container
          v-if="packages.length < 1"
          style="height: 10vh; font-size: larger;"
          class="text-secondary text-capitalize d-flex justify-content-center">
          <b-card-text class="align-self-center font-weight-bold">
            minimum of {{ min }} package is required
          </b-card-text>
        </b-container>

        <draggable
          v-if="packages.length"
          v-model="config.offerGroups[offerGroupIndex].packages"
          :group="`${deviceID}-pkg`"
          handle=".drag-title">
          <transition-group>
            <b-card
              v-for="(pkgItem,i) in packages"
              :key="`${deviceID}-${i}-pkg`"
              class="m-3"
              no-body>
              <b-card-header
                header-tag="header"
                class="p-2 d-flex drag-title"
                role="tab">
                <b-icon
                  icon="grip-vertical"
                  class="align-self-center" />
                <div
                  v-b-toggle="`${deviceID}-${i}-pkg`"
                  class="pl-1 w-100 d-flex justify-content-between align-items-center"
                  @click.prevent="pkgOpen = pkgOpen === i ? null : i">
                  {{ pkgItem.title ? pkgItem.title : "(Blank Package Title)" }}
                  <b-icon
                    :icon="i === pkgOpen ? `chevron-down` : `chevron-right`"
                    class="align-self-center ml-auto"
                    variant="secondary"
                    scale="1.2" />
                </div>
              </b-card-header>
              <b-collapse
                :id="`${deviceID}-${i}-pkg`"
                :accordion="`${deviceID}-pkg`"
                role="tabpanel">
                <b-card-body>
                  <b-form-group>
                    <b-checkbox
                      v-model="pkgItem.enabled"
                      switch>
                      Enabled
                    </b-checkbox>
                  </b-form-group>
                  <b-container v-if="pkgItem.enabled">
                    <paywall-dropdown
                      :required="isRequiredField(pkgItem, 'type')"
                      class="text-capitalize"
                      label="Type"
                      :config="pkgItem"
                      config-key="type" />

                    <paywall-form-input
                      :required="isRequiredField(pkgItem, 'title')"
                      :device="device"
                      :config="pkgItem"
                      :char-max="config.rules.title.charMax"
                      label="Title"
                      config-key="title" />
                    <paywall-form-input
                      :required="isRequiredField(pkgItem, 'subtitle')"
                      :device="device"
                      :config="pkgItem"
                      :char-max="config.rules.subtitle.charMax"
                      label="Subtitle"
                      config-key="subtitle" />
                    <paywall-form-input
                      :required="isRequiredField(pkgItem, 'price')"
                      :device="device"
                      :config="pkgItem"
                      :char-max="config.rules.price.charMax"
                      label="Price"
                      config-key="price" />
                    <paywall-form-input
                      :required="isRequiredField(pkgItem, 'priceUnit')"
                      :device="device"
                      :config="pkgItem"
                      :char-max="config.rules.priceUnit.charMax"
                      label="Price Unit"
                      config-key="priceUnit" />
                    <paywall-form-input
                      :required="isRequiredField(pkgItem, 'text')"
                      :device="device"
                      :config="pkgItem"
                      :char-max="config.rules.text.charMax"
                      label="Text"
                      type="textarea"
                      config-key="text" />
                    <paywall-form-input
                      :required="isRequiredField(pkgItem, 'purchaseLink')"
                      :device="device"
                      :config="pkgItem"
                      :placeholder="pkgItem.purchaseLink"
                      :char-max="config.rules.purchaseLink.charMax"
                      label="Purchase Link"
                      config-key="purchaseLink" />

                    <b-checkbox
                      v-model="pkgItem.requiresTeamSelection"
                      switch>
                      Requires Team Selection
                    </b-checkbox>

                    <b-form-group
                      label="* Size"
                      invalid-feedback="* required"
                      class="mt-3">
                      <b-form-select
                        v-model="pkgItem.size"
                        :options="sizeOptions" />
                    </b-form-group>

                    <paywall-form-package-tags
                      :device="device"
                      :pkg="pkgItem"
                      class="mt-4" />

                    <paywall-form-package-buttons
                      :device="device"
                      :pkg-buttons="pkgItem"
                      class="mt-4" />
                  </b-container>
                </b-card-body>
                <b-button
                  v-if="pkgItem.enabled"
                  class="ml-4 mb-3"
                  variant="danger"
                  :disabled="packages.length <= min"
                  @click="onDeletePkg(i)">
                  Delete Package
                </b-button>
              </b-collapse>
            </b-card>
          </transition-group>
        </draggable>

        <b-button
          :disabled="disableAdd"
          class="mt-3 ml-3"
          variant="success"
          @click="addPkg">
          Add Package
        </b-button>
      </div>

      <paywall-overlay
        :show="showOverlay"
        :msg="overlayMsg"
        @onConfirm="onConfirm"
        @onCancel="onCancel" />
    </div>
  </b-container>
</template>

<script>
import PaywallFormInput from "@/components/PaywallV1/PaywallFormInput"
import PaywallDropdown from "@/components/PaywallV1/PaywallDropdown"
import PaywallOverlay from "@/components/PaywallV1/PaywallOverlay"
import PaywallFormPackageButtons from "@/components/PaywallV1/PaywallFormPackageButtons"
import PaywallFormPackageTags from "@/components/PaywallV1/PaywallFormPackageTags"
import draggable from "vuedraggable"
import _ from "lodash"

export default {
  name: "PaywallFormPackages",
  components: {PaywallOverlay, PaywallDropdown, PaywallFormInput, PaywallFormPackageButtons, PaywallFormPackageTags, draggable},
  props: {
    config: {
      type: Object,
      required: true,
    },
    configKey: {
      type: String,
      required: true,
    },
    device: {
      type: String,
      required: true,
    },
    offerGroupIndex: {
      type: Number,
      required: true,
    },
    placeholder: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      min: "1",
      max: "10",
      showOverlay: false,
      pkgIndexToDelete: null,
      disableAdd: false,
      pkgOpen: null,
      sizeOptions: [
        { text: "Small", value: "small"},
        { text: "Medium", value: "medium"},
        { text: "Large", value: "large"},
      ],
      notRequiredFields: ["subtitle", "purchaseLink"], // Package form fields that are not required for non-free types.
      requiredFreeFields: ["title", "price"], // Package form fields that are required only for free type packages.
    }
  },
  computed: {
    deviceID() {
      return this.device.replaceAll(" ", '')
    },
    overlayMsg() {
      const title = this.pkgIndexToDelete === null ? '' : `${this.packages[this.pkgIndexToDelete].title}`
      return `delete ${this.device}'s ${title} package?`
    },
    packages() {
      return (this.config && this.config.offerGroups && this.config.offerGroups.length > this.offerGroupIndex) ?
          this.config.offerGroups[this.offerGroupIndex].packages : []
    },
  },
  methods: {
    onConfirm() {
      if (this.showOverlay && this.pkgIndexToDelete !== null) {
        this.packages.splice(this.pkgIndexToDelete, 1)
        this.showOverlay = !this.showOverlay
        this.pkgIndexToDelete = null

        if (this.packages.length < this.max) {
          this.disableAdd = false
        }
      }
    },
    onCancel() {
      this.showOverlay = !this.showOverlay
      this.pkgIndexToDelete = null
    },
    addPkg() {
      if (this.packages.length < this.max) {
        this.config.offerGroups[this.offerGroupIndex].packages =
            [...this.packages, _.cloneDeep(this.placeholder.offerGroups[this.offerGroupIndex].packages[0])]
      }

      // Disable add button if max reached.
      if (this.packages.length >= this.max) {
        this.disableAdd = true
        this.$bvToast.toast(`${this.device} supports a maximum of ${this.max} packages`, {
          title: "Cannot Add Package",
          variant: "danger",
          bodyClass: "text-capitalize text-lg",
        })
      }
    },
    onDeletePkg(idx) {
      this.showOverlay = true
      this.pkgIndexToDelete = idx
    },
    isRequiredField(pkg, field) {
      if (pkg.type === "free") {
        return this.requiredFreeFields.includes(field)
      }

      return !this.notRequiredFields.includes(field)
    },
  },
}
</script>

<style scoped>
.drag-title {
  border: 1px solid transparent;
}

.drag-title:hover > div > .bi-chevron-right {
  animation: 0.75s infinite ease-in-out alternate b-icon-animation-cylon;
}

.drag-title:hover {
  border: 1px solid var(--primary);
  transition: all 50ms ease;
  cursor: grab;
}

.drag-title:active, .drag-title:focus {
  cursor: grabbing;
  color: white;
  background: darkgrey;
  transform: scale(1.06);
  transition: transform 50ms ease;
}
</style>
